/**
 * SlotCalendarView Component
 * Calendly-style slot picker: a month calendar highlighting days that have
 * availability; selecting a day reveals that day's times in a side panel
 * (below the calendar on mobile).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Clock, Utensils } from 'lucide-react';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isBefore,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { Button } from '../../button.js';
import { HoverTip } from '../../hover-tip.js';
import { parseDateString } from '../../../lib/date-utils.js';
import { AvailableDay, TimeSlot, BookingData } from '../utils/bookingConstants.js';
import { formatTimeZoneDisplay, formatTimeZoneAbbreviation } from '../utils/bookingTimezones.js';
import { formatSlotCountLabel, formatSlotRange, summarizeDayAvailability } from '../utils/bookingDayHints.js';

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface SlotCalendarViewProps {
  bookingData: BookingData;
  availableDays: AvailableDay[];
  visibleMonth: Date;
  minMonth: Date;
  maxMonth: Date;
  isLoading: boolean;
  onVisibleMonthChange: (month: Date) => void;
  onSlotSelect: (slot: TimeSlot) => void;
}

export function SlotCalendarView({
  bookingData,
  availableDays,
  visibleMonth,
  minMonth,
  maxMonth,
  isLoading,
  onVisibleMonthChange,
  onSlotSelect,
}: SlotCalendarViewProps) {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const timeZone = bookingData.timeZone;

  const daysByDate = useMemo(() => {
    const map = new Map<string, AvailableDay>();
    for (const day of availableDays) {
      if (day.slots.length > 0) {
        // Merged multi-calendar availability arrives unsorted within a day
        map.set(day.date, {
          ...day,
          slots: [...day.slots].sort((a, b) => a.start.localeCompare(b.start)),
        });
      }
    }
    return map;
  }, [availableDays]);

  // Keep the selection valid for the current month's data: auto-select the
  // first available day so times are visible without an extra click.
  useEffect(() => {
    if (selectedDate && daysByDate.has(selectedDate)) return;
    const first = availableDays.find((d) => d.slots.length > 0);
    setSelectedDate(first ? first.date : null);
  }, [availableDays, daysByDate, selectedDate]);

  const calendarWeeks = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(visibleMonth));
    const gridEnd = endOfWeek(endOfMonth(visibleMonth));
    const days = eachDayOfInterval({ start: gridStart, end: gridEnd });
    const weeks: Date[][] = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }
    return weeks;
  }, [visibleMonth]);

  const canGoPrev = isBefore(startOfMonth(minMonth), startOfMonth(visibleMonth));
  const canGoNext = isBefore(startOfMonth(visibleMonth), startOfMonth(maxMonth));

  const selectedDay = selectedDate ? daysByDate.get(selectedDate) : undefined;
  const today = new Date();

  return (
    <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,15rem)] gap-6 md:gap-8">
      {/* Month calendar */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-card-foreground">
            {format(visibleMonth, 'MMMM yyyy')}
          </h4>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Previous month"
              disabled={!canGoPrev || isLoading}
              onClick={() => onVisibleMonthChange(addMonths(startOfMonth(visibleMonth), -1))}
              className="rounded-full"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Next month"
              disabled={!canGoNext || isLoading}
              onClick={() => onVisibleMonthChange(addMonths(startOfMonth(visibleMonth), 1))}
              className="rounded-full"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-7 mb-2">
          {WEEKDAY_LABELS.map((label) => (
            <div
              key={label}
              className="text-center text-xs font-medium uppercase tracking-wide text-gray-500 py-1"
            >
              {label}
            </div>
          ))}
        </div>

        <div className={`space-y-1 ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}>
          {calendarWeeks.map((week, weekIndex) => (
            <div key={weekIndex} className="grid grid-cols-7 gap-1">
              {week.map((day) => {
                const dateKey = format(day, 'yyyy-MM-dd');
                const inMonth = isSameMonth(day, visibleMonth);
                const available = inMonth && daysByDate.has(dateKey);
                const isSelected = available && selectedDate === dateKey;
                const isToday = isSameDay(day, today);

                if (!inMonth) {
                  return <div key={dateKey} aria-hidden className="aspect-square" />;
                }

                // Hover/focus summary: how many times the day holds and the
                // span they cover, so the month is scannable without a click.
                // Unavailable days stay `disabled` (and so emit no pointer
                // events) — their aria-label already says there is nothing to
                // see, and the tip would carry no new information.
                const summary = summarizeDayAvailability(daysByDate.get(dateKey), timeZone);
                const dayName = format(day, 'EEEE, MMMM d');

                const cell = (
                  <button
                    key={dateKey}
                    type="button"
                    disabled={!available}
                    onClick={() => setSelectedDate(dateKey)}
                    aria-pressed={isSelected}
                    aria-label={`${dayName} — ${available ? summary.countLabel : 'no times available'}`}
                    className={`aspect-square w-full max-w-12 mx-auto rounded-full text-sm flex items-center justify-center transition-colors relative ${
                      isSelected
                        ? 'bg-blue-600 text-white font-semibold shadow-sm'
                        : available
                          ? 'bg-blue-50 text-blue-700 font-semibold hover:bg-blue-100'
                          : 'text-gray-300 cursor-default'
                    } ${isToday && !isSelected ? 'ring-1 ring-blue-300' : ''}`}
                  >
                    {format(day, 'd')}
                    {available && !isSelected && (
                      <span className="absolute bottom-1 w-1 h-1 rounded-full bg-blue-500" />
                    )}
                  </button>
                );

                if (!available) return cell;

                return (
                  <HoverTip
                    key={dateKey}
                    // The count already lives in the button's aria-label above,
                    // so the tip must not overwrite that accessible name.
                    label={null}
                    tip={
                      <span className="block">
                        <span className="block font-semibold">{dayName}</span>
                        <span className="block">{summary.hint}</span>
                        {summary.lunchSlotCount > 0 && (
                          <span className="block text-gray-500 dark:text-gray-400">
                            {summary.lunchSlotCount} over lunch
                          </span>
                        )}
                      </span>
                    }
                  >
                    {cell}
                  </HoverTip>
                );
              })}
            </div>
          ))}
        </div>

        <p className="mt-4 text-xs text-gray-500">
          Times shown in {formatTimeZoneDisplay(timeZone)}.
        </p>
      </div>

      {/* Times for the selected day */}
      <div className="md:border-l md:border-gray-200 md:pl-6">
        {selectedDay ? (
          <>
            <div className="mb-3">
              <div className="font-semibold text-card-foreground">
                {parseDateString(selectedDay.date).toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                })}
              </div>
              <div className="text-sm text-gray-500">
                {formatSlotCountLabel(selectedDay.slots.length)} ·{' '}
                {formatTimeZoneAbbreviation(timeZone)}
              </div>
            </div>
            <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
              {selectedDay.slots.map((slot, index) => {
                const isSelected = bookingData.selectedSlot?.start === slot.start;
                // The pill shows only a start time. The hover/focus tip adds the
                // end and the zone, so a booker can tell a 30-minute slot from a
                // 60-minute one before committing, and knows which clock it is on.
                const slotRange = formatSlotRange(slot, timeZone);
                const slotTip = slotRange
                  ? slot.isLunchSlot
                    ? `${slotRange} · over lunch`
                    : slotRange
                  : null;
                return (
                  <HoverTip
                    key={`${selectedDay.date}-${index}`}
                    tip={slotTip}
                    label={slotTip ? `Book ${slotTip}` : null}
                    side="left"
                  >
                    <button
                      type="button"
                      onClick={() => onSlotSelect(slot)}
                      className={`w-full flex items-center justify-center gap-2 rounded-lg border py-2.5 px-3 text-sm font-semibold transition-colors ${
                        isSelected
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : slot.isLunchSlot
                            ? 'border-orange-300 text-orange-700 hover:bg-orange-50'
                            : 'border-blue-200 text-blue-700 hover:border-blue-500 hover:bg-blue-50'
                      }`}
                    >
                      {slot.isLunchSlot ? (
                        <Utensils className="w-3.5 h-3.5 shrink-0" />
                      ) : (
                        <Clock className="w-3.5 h-3.5 shrink-0" />
                      )}
                      {formatInTimeZone(parseISO(slot.start), timeZone, 'h:mm a')}
                    </button>
                  </HoverTip>
                );
              })}
            </div>
          </>
        ) : (
          <div className="h-full flex items-center justify-center text-sm text-gray-500 text-center py-8">
            {isLoading
              ? 'Loading availability…'
              : 'No times available this month. Try the next month.'}
          </div>
        )}
      </div>
    </div>
  );
}
